import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useDimensions } from './useDimensions';

const processDataset = (data) => {
    return data.reduce((acc, record) => {
        const year = new Date(record.date).getFullYear();
        if (!acc[year]) {
            acc[year] = 0;
        }
        acc[year] += 1
        return acc;
    }, {});
}

// const calcPercentageChange = (data) => {
//     let lastKnown = { china: null, usa: null, japan: null, taiwan: null }; // Store last known values

//     return data.map((yearData) => {
//         let change = { year: yearData.year };

//         ['china', 'usa', 'japan', 'taiwan'].forEach((country) => {
//             if (yearData[country] === null) {
//                 change[country] = null;
//             } else if (lastKnown[country] === null) {
//                 change[country] = null;
//                 lastKnown[country] = yearData[country];
//             } else {
//                 // Calculate percentage change using last known value
//                 change[country] = ((yearData[country] - lastKnown[country]) / lastKnown[country]) * 100;
//                 lastKnown[country] = yearData[country];
//             }
//             // Update last known value
//             if (yearData[country] !== null) {
//                 lastKnown[country] = yearData[country];
//             }
//         });

//         return change;
//     });
// };

const calcPercentageChange = (data) => {
    let lastKnown = { china: null, usa: null, japan: null, taiwan: null }; // Store last known values
    let isFirst = { china: true, usa: true, japan: true, taiwan: true }; // Track first appearance
    

    return data.map((yearData) => {
        let change = { year: yearData.year };

        ['china', 'usa', 'japan', 'taiwan'].forEach((country) => {
            if (yearData[country] === null) {
                // If current year has no data, set change to null
                change[country] = null;
            } else if (isFirst[country]) {
                // If it's the first data point for this country, set change to 0
                change[country] = 0;
                lastKnown[country] = yearData[country];
                isFirst[country] = false;
            } else if (lastKnown[country] === null) {
                // If no previous data but not first appearance, set to null
                change[country] = null;
                lastKnown[country] = yearData[country];
            } else {
                // Calculate percentage change using last known value
                change[country] = ((yearData[country] - lastKnown[country]) / lastKnown[country]) * 100;
                lastKnown[country] = yearData[country];
            }
        });

        return change;
    });
};

const AttacksByCountry = ({ chinaData, usaData, japanData, taiwanData }) => {

        const containerRef = useRef(null);
        const dimensions = useDimensions(containerRef);

        const svgRef = useRef();

        const chinaIncidents = processDataset(chinaData);
        const usaIncidents = processDataset(usaData);
        const japanIncidents = processDataset(japanData);
        const taiwanIncidents = processDataset(taiwanData);

        // const allYears = [...new Set([...Object.keys(chinaIncidents), ...Object.keys(usaIncidents), ...Object.keys(japanIncidents), ...Object.keys(taiwanIncidents)])];

        // Get all years from all datasets and filter out years with no data for all countries
        const allYears = [
            ...new Set([
                ...Object.keys(chinaIncidents),
                ...Object.keys(usaIncidents),
                ...Object.keys(japanIncidents),
                ...Object.keys(taiwanIncidents),
            ])
        ];

        const combinedData = allYears
            .map((year) => ({
                year: parseInt(year),
                china: chinaIncidents[year] || null,
                usa: usaIncidents[year] || null,
                japan: japanIncidents[year] || null,
                taiwan: taiwanIncidents[year] || null,
            }))
            .filter((data) => data.china == null || data.usa == null || data.japan == null || data.taiwan == null);

        combinedData.sort((a, b) => a.year - b.year);
        const percentageChangeData = calcPercentageChange(combinedData);
        percentageChangeData.sort((a, b) => a.year - b.year);
        
    

    useEffect(() => {

        const margin = { top: 35, right: 15, bottom: 40, left: 40 };
        // const width = 800 - margin.left - margin.right;
        // const height = 400 - margin.top - margin.bottom;
        const { width, height } = dimensions;

        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr('height', height + margin.bottom + margin.top)
            .append("g")
            .attr("transform", `translate(${margin.left}, ${margin.top})`);

        const xScale = d3.scaleLinear()
            .domain([2000,2025])
            .range([6, width - margin.right - margin.left]);


        const minY = d3.min(percentageChangeData, d => Math.min(d.china, d.usa, d.japan, d.taiwan)) || 0;
        const maxY = d3.max(percentageChangeData, d => Math.max(d.china, d.usa, d.japan, d.taiwan)) || 0;
            
        const yScale = d3.scaleLinear()
            .domain([minY, maxY])
            .nice()
            .range([height - 30, 0]);

        // const line = d3.line()
        //     .x((d) => xScale(d.year))
        //     .y((d) => yScale(d.china));

        svg.append("g")
            .attr("class", "svg-grid")
            .call(
            d3.axisLeft(yScale)
            .tickSize(-width + margin.right + margin.left)
            .tickFormat("")
            )
            .select(".domain")
            .attr("stroke", "none");

        const xAxis = d3.axisBottom(xScale)
            .tickFormat(d3.format("d"))
            .tickSize(0)
            .ticks(width / 100);

        const yAxis = d3.axisLeft(yScale)
            .tickSize(0)
            .tickPadding(10)
            .tickFormat(d => `${d}%`);

        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${yScale(0)})`)
            .call(xAxis)
            .selectAll("text")
            .attr("transform", `translate(0,85)`);

        svg.append("g")
            .attr("class", "y-axis")
            // .attr("transform", `translate(${margin.left + 100},0)`)
            .call(yAxis)
            .select(".domain")
            .attr("opacity", 0);

        svg.append("text")
            .attr("class", "axes")
            .attr("y", height - margin.bottom / 3 + 50 )
            .attr("x", xScale(2012) )
            .style('text-anchor', 'middle')
            .text('年份');

        svg.append('text')
            .attr("class","axes")
            .attr("x", -37)
            .attr("y", -22)
            .text("百分比变化");


        const Tooltip = d3.select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("opacity", 0);
        
        //mouse over for dots only
        const handleMouseOver = (event, d, country) => {
            Tooltip.style("opacity", 1);
            d3.selectAll(".line, .dot")
                .style("opacity", 0.2)
            d3.selectAll(`.${country}-line, .${country}-dot`)
                .style("opacity", 1)
                .attr("cursor", "pointer");

            d3.select(event.currentTarget)
                .raise()
                .transition()
                .duration(200)
                .style("opacity", 1)
                .attr("r", 5)
                .attr("cursor", "pointer")
                .attr("stroke", "white")
                .attr("stroke-width", 1);
        };

        const handleLineMouseOver = (event, country) => {
            Tooltip.style("opacity", 0); // Hide tooltip

            d3.selectAll(".line, .dot")
                .style("opacity", 0.2)
                .style("pointer-events", "none");
        
            d3.selectAll(`.${country}-line, .${country}-dot`)
                .raise()
                .style("opacity", 1) 
                .attr("cursor", "pointer")
                .style("pointer-events", "auto");
        };

        const countriesCn = {
            china: "中国",
            usa: "美国",
            japan: "日本",
            taiwan: "台湾"
        };

        const handleMouseMove = (event, d, country) => {
            const countryCn = countriesCn[country];

            const currentYearData = combinedData.find(dataPoint => dataPoint.year === d.year); // Get the current year's data

            const lastValidDataPoint = combinedData.filter(dataPoint =>
                dataPoint.year < d.year &&
                dataPoint[country] != null && !isNaN(dataPoint[country])
            ).pop();

            let tooltipText = `
                    <span class="tooltip__num"><b>${d.year}</b></span> <br>
                    <span style="color:var(--${country}"><b>${countryCn}</b></span> <span class="tooltip__num">${(d[country] > 0 ? "+" : "") + (d[country]).toFixed(1)}%</span><br>
                      `;

            if (currentYearData) {
                tooltipText += `<b>${currentYearData[country]}</b><span class="tooltip__sm"> 起攻击事件</span>`; // Add current year incidents
            }

            if (lastValidDataPoint) {
            tooltipText += ` <span class="tooltip__sm">(相比</span>${lastValidDataPoint.year}<span class="tooltip__sm">年的</span><b>${lastValidDataPoint[country]}</b><span class="tooltip__sm">起)</span>`; // Add previous year incidents
            } else {
                tooltipText += `<span class="tooltip__sm"> (没有先前数据)</span>`; // Handle missing previous data
            }

            Tooltip
                .html(tooltipText)
                // .html(`
                //     ${d.year} <br>
                //     <span style="color:var(--${country}"><b>${countryCn}</b></span>
                //     ${d[country]}% (${percentageChangeText})
                //     `)
                .style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 10) + "px")
                .style("opacity", 1);
            d3.selectAll(`.${country}-line`)
                .style("opacity", 1);
        };

        const handleMouseOut = () => {
            Tooltip.style("opacity", 0);
            d3.selectAll(".line, .dot")
                .style("opacity", 1)
                .style("pointer-events", "auto");
            d3.selectAll(".dot")
                .attr("r", 4)
                .attr("stroke", "none");
        };

        const countries = ['china', 'usa', 'japan', 'taiwan'];
        const colors = { china: "var(--china)", usa: "var(--usa)", japan: "var(--japan)", taiwan: "var(--taiwan)" };

        countries.forEach(country => {

            // const validLineData = percentageChangeData.filter(d => d[country] !== null);

            svg.append("path")
                .data([percentageChangeData])
                .attr("class", `${country}-line line`)
                .attr("fill", "none")
                .attr('stroke', colors[country])
                .attr('stroke-width', 2)
                .attr("d", d3.line()
                    .x(d => xScale(d.year))
                    // .y(d => yScale(d[country]))
                    .y(d => {
                        // If current point has data, use it
                        if (d[country] !== null) {
                            return yScale(d[country]);
                        }
                        // If current point is null, look for nearest valid points
                        const index = percentageChangeData.indexOf(d);
                        
                        // Find previous valid point
                        let prevIndex = index - 1;
                        while (prevIndex >= 0 && percentageChangeData[prevIndex][country] === null) {
                            prevIndex--;
                        }
                        
                        // Find next valid point
                        let nextIndex = index + 1;
                        while (nextIndex < percentageChangeData.length && percentageChangeData[nextIndex][country] === null) {
                            nextIndex++;
                        }
                        
                        // If we have both prev and next points, interpolate
                        if (prevIndex >= 0 && nextIndex < percentageChangeData.length) {
                            const prevValue = percentageChangeData[prevIndex][country];
                            const nextValue = percentageChangeData[nextIndex][country];
                            const prevYear = percentageChangeData[prevIndex].year;
                            const nextYear = percentageChangeData[nextIndex].year;
                            
                            // Linear interpolation
                            const ratio = (d.year - prevYear) / (nextYear - prevYear);
                            const interpolatedValue = prevValue + (nextValue - prevValue) * ratio;
                            return yScale(interpolatedValue);
                        }
                        
                        return null;
                    })
                    .defined(d => {
                        const index = percentageChangeData.indexOf(d);
                        
                        if (d[country] !== null) return true;
                        
                        // Check if this null point has valid points before and after
                        const hasPrev = percentageChangeData.slice(0, index)
                            .some(point => point[country] !== null);
                        const hasNext = percentageChangeData.slice(index + 1)
                            .some(point => point[country] !== null);
                        
                        return hasPrev && hasNext;
                    })
        
                )
                .on("mouseover", (event, d) =>  { handleLineMouseOver(event, country) })
                .on("mousemove", (event, d) =>  { handleLineMouseOver(event, country) })
                .on("mouseout", handleMouseOut);

            svg.selectAll(`.${country}-dot dot`)
                .data(percentageChangeData.filter(d => d[country] != null && !isNaN(d[country])))
                .enter()
                .append("circle")
                .attr("class", `${country}-dot dot`)
                .attr("cx", d => xScale(d.year))
                .attr("cy", d => yScale(d[country]))
                .attr("r", d => d[country] === null ? 0 : 4)
                .attr("fill", colors[country])
                .on("mouseover", (event, d) => { handleMouseOver(event, d, country) })
                .on("mousemove", (event, d) => { handleMouseMove(event, d, country) })
                .on("mouseout", handleMouseOut);
        });

        


        // console.log(percentageChangeData);
        // console.log('combinedData:', JSON.stringify(combinedData, null, 2));
        // console.log('p change d:', JSON.stringify(percentageChangeData, null, 2));
        

    }, [percentageChangeData, dimensions, combinedData]);

  return (
    <div className='center-container'>

        <div ref={containerRef} style={{ height:"500px" }} className='container-ref'>
            <h4 style={{ paddingBottom:"15px"}}>与<span className="usa">美国</span>、<span className="taiwan">台湾</span>、<span className="japan">日本</span>比较变化趋势</h4>
            <svg ref={svgRef}></svg>
        </div>
        <div className='spacerdiv__tall' />

        <div className='article-body'>
            <p>对比其它国家，日本和台湾无差别袭击事件每年变化不显著，平均每年在1-2件左右（日本1.5起， 台湾1.9起）。美国则在2021年达到顶峰, 有8起案件发生。专家在<a href="https://www.bbc.com/news/world-us-canada-64377360" target="_blank">解释</a>美国大型枪击案频发的原因说，由于其“难以预测”的属性，很难知道确切的根源，但高拥枪率、生活压力，因疫情而引发的经济家庭和就业困境都是可能的诱因。</p>
            <p>“显然，疫情期间累计了不同类型的社会问题。” 伊莱·弗里德曼（Eli Friedman）是康奈尔大学研究中国劳工问题的社会学教授，社会问题的爆发往往存在滞后性, “不是封锁结束就自动消失了。”</p>

            <p>我们很难用单一原因去解释近年来中国无差别袭击事件所出现的高峰。正如康奈尔大学副教授 Peidong Song 在《外交事务》<i>(Foreign Affairs)</i> 杂志写道，“这些攻击揭示了中国社会深层次和大面积的裂痕，原因包括经济停滞、制度性不平等以及社会的流动性和排斥。由此，这类事件也被称为‘报复社会’的攻击。”</p>
        </div>
    </div>
  )
}

export default AttacksByCountry;